/**
 * Progress Bar Components for NCR Edit
 */

import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Check, ChevronRight } from 'lucide-react';
import { cn } from '../../lib/utils';
import { ProgressStep } from './types';
import { WORKFLOW_STEPS } from './constants';

interface Template1ProgressBarProps {
  currentStep: number;
}

export function Template1ProgressBar({ currentStep }: Template1ProgressBarProps) {
  return (
    <Card>
      <CardContent className="pt-6 pb-6">
        <div className="relative">
          {/* Progress Line Background */}
          <div
            className="absolute top-5 left-0 right-0 h-0.5 bg-gray-200 dark:bg-gray-700"
            style={{ width: 'calc(100% - 64px)', marginLeft: '32px' }}
          />

          {/* Active Progress Line */}
          <div
            className="absolute top-5 left-0 h-0.5 bg-blue-600 dark:bg-blue-500 transition-all duration-300"
            style={{
              width: `calc(${((currentStep - 1) / 3) * 100}% - ${64 - ((currentStep - 1) / 3) * 64}px)`,
              marginLeft: '32px',
            }}
          />

          {/* Steps */}
          <div className="relative flex items-start justify-between">
            {WORKFLOW_STEPS.map((step) => {
              const isCompleted = currentStep > step.num;
              const isCurrent = currentStep === step.num;

              return (
                <div key={step.num} className="flex flex-col items-center" style={{ width: '160px' }}>
                  {/* Step Circle */}
                  <div
                    className={cn(
                      'flex h-10 w-10 items-center justify-center rounded-full border-2 transition-colors',
                      isCompleted
                        ? 'bg-green-700 border-green-700 dark:bg-green-500 dark:border-green-500'
                        : isCurrent
                          ? 'bg-blue-600 border-blue-600 dark:bg-blue-500 dark:border-blue-500'
                          : 'bg-white border-gray-300 dark:bg-gray-800 dark:border-gray-600'
                    )}
                  >
                    {isCompleted ? (
                      <Check className="h-5 w-5 text-white" strokeWidth={2.5} />
                    ) : (
                      <span
                        className={cn(
                          'text-sm font-semibold',
                          isCurrent ? 'text-white' : 'text-gray-500 dark:text-gray-400'
                        )}
                      >
                        {step.num}
                      </span>
                    )}
                  </div>

                  {/* Step Label */}
                  <div className="mt-3 text-center">
                    <p
                      className={cn(
                        'text-sm font-semibold',
                        isCompleted
                          ? 'text-green-800 dark:text-green-400'
                          : isCurrent
                            ? 'text-blue-700 dark:text-blue-300'
                            : 'text-gray-700 dark:text-gray-300'
                      )}
                    >
                      {step.label}
                    </p>
                    <p
                      className={cn(
                        'text-xs mt-0.5',
                        isCompleted || isCurrent
                          ? 'text-gray-700 dark:text-gray-400'
                          : 'text-gray-600 dark:text-gray-400'
                      )}
                    >
                      {step.sublabel}
                    </p>
                    <Badge
                      variant="outline"
                      className={cn(
                        'mt-1.5 text-xs font-medium',
                        step.role === 'Customer'
                          ? 'bg-white text-black border-purple-300 dark:bg-gray-800 dark:text-white dark:border-purple-700'
                          : 'bg-white text-black border-orange-300 dark:bg-gray-800 dark:text-white dark:border-orange-700'
                      )}
                    >
                      {step.role}
                    </Badge>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

interface Template2ProgressBarProps {
  steps: ProgressStep[];
}

export function Template2ProgressBar({ steps }: Template2ProgressBarProps) {
  return (
    <Card>
      <CardContent className="py-6">
        <div className="flex flex-wrap items-center justify-center gap-2">
          {steps.map((step, index) => (
            <div key={index} className="flex items-center">
              {/* Step */}
              <div className="flex flex-col items-center">
                <div
                  className={cn(
                    'w-12 h-12 rounded-full flex items-center justify-center text-xl transition-all duration-300',
                    step.state === 'completed' ? 'bg-green-600 text-white' : 'bg-gray-200 text-gray-500'
                  )}
                >
                  {step.icon}
                </div>
                <span
                  className={cn(
                    'mt-2 text-xs font-medium text-center max-w-[80px]',
                    step.state === 'completed' ? 'text-green-700' : 'text-gray-500'
                  )}
                >
                  {step.title}
                </span>
              </div>

              {/* Arrow (except for last step) */}
              {index < steps.length - 1 && (
                <ChevronRight
                  className={cn(
                    'h-6 w-6 mx-2 transition-colors duration-300',
                    step.state === 'completed' ? 'text-green-500' : 'text-gray-300'
                  )}
                />
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
